import { useEffect, useMemo, useRef, useState } from "react";
import { X, Upload, Loader2, Plus, Trash2, AlertCircle, CheckCircle2, ArrowLeft, ArrowRight, RefreshCw, Users, Info } from "lucide-react";
import {
  INVESTOR_FIELDS, RESOLUTION_OPTIONS, parseCSV, normalizeRows, emptyRow, defaultSelection, canSelect,
  effectiveSelection, resolutionsFor, isReady,
  type ImportRow, type PreviewResponse, type PreviewRow, type CommitResponse, type Resolution, type InvestorField,
} from "@/lib/cohort-import";

const BASE = import.meta.env.BASE_URL;
function authHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", "x-admin-token": localStorage.getItem("admin_token") || "" };
}
async function readError(r: Response): Promise<string> {
  const b = await r.json().catch(() => null);
  return b?.error?.message || b?.message || (typeof b?.error === "string" ? b.error : "") || `Request failed (${r.status})`;
}

type Cohort = { id: number; name: string; slug: string; status: string };
type Step = 1 | 2 | 3 | 4;

// Explicit opaque palette — never relies on inherited theme tokens.
const C = {
  panel: "bg-[#FAF8F4] text-[#1F1B16] dark:bg-[#17140F] dark:text-[#F2EDE4]",
  sub: "bg-[#F1EDE5] dark:bg-[#211D17]",
  border: "border-[#D9D1C3] dark:border-[#3A332A]",
  muted: "text-[#5E5548] dark:text-[#B9AE9C]",
  input: "bg-[#FFFDF9] text-[#1F1B16] border-[#C9BFAE] placeholder:text-[#8A8072] dark:bg-[#0F0D0A] dark:text-[#F2EDE4] dark:border-[#4A4136] dark:placeholder:text-[#857A69] focus:outline-none focus:ring-2 focus:ring-[#FF5C00]",
  primary: "bg-[#C64600] hover:bg-[#A83B00] text-[#FFF8F2] disabled:bg-[#BFB5A6] disabled:text-[#4A4136] dark:disabled:bg-[#3A332A] dark:disabled:text-[#9C917F]",
  ghost: "bg-[#FFFDF9] hover:bg-[#EDE7DC] text-[#1F1B16] border border-[#C9BFAE] dark:bg-[#211D17] dark:hover:bg-[#2C261E] dark:text-[#F2EDE4] dark:border-[#4A4136]",
  focus: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5C00] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF8F4] dark:focus-visible:ring-offset-[#17140F]",
};

const STATUS_META: Record<PreviewRow["status"], { label: string; cls: string }> = {
  new: { label: "New investor", cls: "bg-[#DDF1E4] text-[#14532D] border-[#9BCFAE] dark:bg-[#12301F] dark:text-[#A7E3BC] dark:border-[#2B6B45]" },
  existing: { label: "Existing, will join", cls: "bg-[#DCE8F7] text-[#1E3A6B] border-[#A3BFE3] dark:bg-[#15243B] dark:text-[#B5CDF0] dark:border-[#33507D]" },
  already_member: { label: "Already member", cls: "bg-[#E8E4DC] text-[#3F382E] border-[#C9BFAE] dark:bg-[#2A251E] dark:text-[#D6CCBB] dark:border-[#4A4136]" },
  review: { label: "Needs decision", cls: "bg-[#FCEBC7] text-[#6B4300] border-[#E8C277] dark:bg-[#3A2A0C] dark:text-[#F5D38A] dark:border-[#7A5A1C]" },
  blocked: { label: "Blocked", cls: "bg-[#F8DAD6] text-[#7A1D12] border-[#E5A39A] dark:bg-[#3D1712] dark:text-[#F4B3A9] dark:border-[#7D3328]" },
  duplicate: { label: "Duplicate in CSV", cls: "bg-[#EBDDF2] text-[#4E2463] border-[#C9A8DB] dark:bg-[#2C1A36] dark:text-[#DCC0EA] dark:border-[#5C3A70]" },
};

const STEPS = ["Cohort", "Rows", "Review", "Done"];

export default function CohortImportDialog({ onClose, onComplete }: { onClose: () => void; onComplete: () => void }) {
  const [step, setStep] = useState<Step>(1);
  const [cohorts, setCohorts] = useState<Cohort[] | null>(null);
  const [cohortError, setCohortError] = useState("");
  const [cohortId, setCohortId] = useState<number | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [filename, setFilename] = useState("");
  const [ignored, setIgnored] = useState<string[]>([]);
  const [fileError, setFileError] = useState("");
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [resolutions, setResolutions] = useState<Record<string, string>>({});
  const [ack, setAck] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<CommitResponse | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const busy = previewing || committing;
  const busyRef = useRef(busy); busyRef.current = busy;
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;

  const loadCohorts = async () => {
    setCohortError(""); setCohorts(null);
    try {
      const r = await fetch(`${BASE}api/cohorts`, { headers: authHeaders() });
      if (!r.ok) throw new Error(await readError(r));
      const body = await r.json();
      setCohorts(Array.isArray(body) ? body : []);
    } catch (e) { setCohortError(e instanceof Error ? e.message : "Could not load cohorts."); setCohorts([]); }
  };
  useEffect(() => { void loadCohorts(); }, []);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busyRef.current) onCloseRef.current();
      if (e.key === "Tab" && dialogRef.current) {
        const f = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]');
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.(); };
  }, []);

  const cohort = cohorts?.find(c => c.id === cohortId) || null;

  const invalidate = () => { setPreview(null); setSelected(new Set()); setResolutions({}); setAck(false); setError(""); };

  const onFile = async (file: File) => {
    setFileError("");
    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      const n = normalizeRows(parsed.headers, parsed.rows);
      if (n.missing.length) { setFileError(`Missing required column${n.missing.length > 1 ? "s" : ""}: ${n.missing.join(", ")}. Download the template for the expected headers.`); return; }
      if (!n.rows.length) { setFileError("This file has headers but no data rows."); return; }
      setFilename(file.name); setIgnored(n.ignored); setRows(n.rows); invalidate();
    } catch { setFileError("Could not read this file. Save it as UTF-8 CSV and try again."); }
  };

  const editRow = (i: number, field: InvestorField, v: string) => { setRows(rs => rs.map((r, j) => j === i ? { ...r, [field]: v } : r)); invalidate(); };
  const removeRow = (i: number) => { setRows(rs => rs.filter((_, j) => j !== i)); invalidate(); };
  const addRow = () => { setRows(rs => [...rs, emptyRow()]); invalidate(); };

  const runPreview = async () => {
    if (!cohortId) return;
    setPreviewing(true); setError("");
    try {
      const r = await fetch(`${BASE}api/admin/investors/cohort-import/preview`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ cohortId, rows }) });
      if (!r.ok) throw new Error(await readError(r));
      const p: PreviewResponse = await r.json();
      setPreview(p); setSelected(defaultSelection(p.rows)); setResolutions({}); setAck(false); setStep(3);
    } catch (e) { setError(e instanceof Error ? e.message : "Preview failed."); }
    finally { setPreviewing(false); }
  };

  const finalSelection = useMemo(() => preview ? effectiveSelection(preview.rows, selected, resolutions) : [], [preview, selected, resolutions]);
  const skipped = preview ? preview.rows.length - finalSelection.length : 0;
  const unresolvedSelected = preview ? preview.rows.filter(r => selected.has(r.index) && !canSelect(r, resolutions)).length : 0;

  const commit = async () => {
    if (!preview || !cohortId || !ack || finalSelection.length === 0) return;
    setCommitting(true); setError("");
    try {
      const r = await fetch(`${BASE}api/admin/investors/cohort-import/commit`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ cohortId, rows, reviewHash: preview.reviewHash, selectedRows: finalSelection, resolutions: resolutionsFor(preview.rows, finalSelection, resolutions) }),
      });
      if (r.status === 409) { invalidate(); setError("Investor or cohort data changed since this preview. Nothing was saved. Run the preview again to review current data."); setStep(2); return; }
      if (!r.ok) throw new Error(`Nothing was saved. ${await readError(r)}`);
      setResult(await r.json()); setStep(4); onComplete();
    } catch (e) { setError(e instanceof Error ? e.message : "Import failed. Nothing was saved."); }
    finally { setCommitting(false); }
  };

  const toggle = (row: PreviewRow) => {
    if (!canSelect(row, resolutions)) return;
    setSelected(s => { const n = new Set(s); n.has(row.index) ? n.delete(row.index) : n.add(row.index); return n; });
    setAck(false);
  };
  const setResolution = (row: PreviewRow, v: string) => {
    setResolutions(rs => { const n = { ...rs }; if (v) n[String(row.index)] = v; else delete n[String(row.index)]; return n; });
    setSelected(s => { const n = new Set(s); v ? n.add(row.index) : n.delete(row.index); return n; });
    setAck(false);
  };
  const jumpToEdit = (rowIndex: number) => {
    setStep(2);
    setTimeout(() => document.getElementById(`ci-row-${rowIndex}-name`)?.focus(), 50);
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-[#0B0907]/75 flex items-end sm:items-center justify-center sm:p-4" onMouseDown={e => e.target === e.currentTarget && !busy && onClose()}>
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="ci-title" data-testid="dialog-cohort-import"
        className={`${C.panel} border ${C.border} rounded-t-2xl sm:rounded-2xl w-full min-w-0 max-w-5xl max-h-[95dvh] sm:max-h-[90dvh] flex flex-col shadow-2xl outline-none [overflow-wrap:anywhere]`}>
        {/* Header */}
        <div className={`border-b ${C.border} px-5 sm:px-6 py-4 flex items-start justify-between gap-4`}>
          <div className="min-w-0">
            <h2 id="ci-title" className="text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-[#C64600] dark:text-[#FF7A2E]" aria-hidden /> Import to cohort</h2>
            <p className={`${C.muted} text-sm mt-0.5`}>Adds investor profiles and cohort memberships only. No allocations are created and no emails are sent.</p>
          </div>
          <button onClick={onClose} disabled={busy} className={`p-2 rounded-lg ${C.ghost} ${C.focus}`} aria-label="Close import" data-testid="button-close-import"><X className="w-4 h-4" /></button>
        </div>
        <ol className={`flex flex-wrap gap-1 px-5 sm:px-6 py-3 border-b ${C.border} ${C.sub} text-xs font-medium`} aria-label="Import progress">
          {STEPS.map((s, i) => {
            const n = i + 1; const active = n === step; const done = n < step;
            return (
              <li key={s} aria-current={active ? "step" : undefined} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full whitespace-nowrap ${active ? "bg-[#C64600] text-[#FFF8F2]" : done ? "text-[#14532D] dark:text-[#A7E3BC]" : C.muted}`}>
                <span className={`w-5 h-5 grid place-items-center rounded-full text-[11px] ${active ? "bg-[#FFF8F2] text-[#C64600]" : `border ${C.border}`}`}>{done ? <CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> : n}</span>{s}
              </li>
            );
          })}
        </ol>

        <div className="flex-1 min-w-0 overflow-y-auto px-5 sm:px-6 py-5 space-y-4">
          {error && <Alert tone="error" testid="text-import-error">{error}</Alert>}

          {step === 1 && (
            <section className="space-y-3" aria-labelledby="ci-s1">
              <h3 id="ci-s1" className="font-semibold">Choose the cohort these investors join</h3>
              <p className={`${C.muted} text-sm`}>Existing memberships in other cohorts are preserved. Company allocations stay in Manage allocations.</p>
              {cohortError && <Alert tone="error">{cohortError} <button onClick={loadCohorts} className="underline font-semibold ml-1">Retry</button></Alert>}
              {cohorts === null ? (
                <div className="grid sm:grid-cols-2 gap-2">{[0, 1, 2, 3].map(i => <div key={i} className={`h-16 rounded-xl animate-pulse ${C.sub}`} />)}</div>
              ) : cohorts.length === 0 && !cohortError ? (
                <div className={`rounded-xl border ${C.border} ${C.sub} p-6 text-center text-sm ${C.muted}`}>No cohorts exist yet. Create a cohort first, then return here.</div>
              ) : (
                <div role="radiogroup" aria-label="Cohort" className="grid sm:grid-cols-2 gap-2">
                  {cohorts.map(c => {
                    const on = c.id === cohortId;
                    return (
                      <label key={c.id} data-testid={`option-cohort-${c.id}`} className={`cursor-pointer rounded-xl border-2 p-3.5 flex items-center gap-3 ${on ? "border-[#C64600] bg-[#FDEBDD] dark:bg-[#3A1F0E]" : `${C.border} bg-[#FFFDF9] dark:bg-[#1C1914] hover:border-[#A89C88]`}`}>
                        <input type="radio" name="cohort" className="accent-[#C64600] w-4 h-4" checked={on} onChange={() => { setCohortId(c.id); invalidate(); }} />
                        <span className="min-w-0 flex-1"><span className="block font-semibold truncate">{c.name}</span><span className={`block text-xs font-mono ${C.muted}`}>{c.slug}</span></span>
                        <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded border ${C.border} ${C.muted}`}>{c.status}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {step === 2 && (
            <section className="space-y-3" aria-labelledby="ci-s2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 id="ci-s2" className="font-semibold">Rows for <span className="text-[#C64600] dark:text-[#FF7A2E]">{cohort?.name}</span></h3>
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = ""; }} data-testid="input-csv-file" />
                  <button onClick={() => fileRef.current?.click()} className={`px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 ${C.ghost} ${C.focus}`} data-testid="button-choose-csv"><Upload className="w-4 h-4" /> {rows.length ? "Replace CSV" : "Choose CSV"}</button>
                  <button onClick={addRow} className={`px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 ${C.ghost} ${C.focus}`} data-testid="button-add-row"><Plus className="w-4 h-4" /> Add row</button>
                </div>
              </div>
              <p className={`${C.muted} text-xs flex gap-1.5`}><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden />The file is read in this browser only. Nothing is sent until you run the preview.</p>
              {fileError && <Alert tone="error">{fileError}</Alert>}
              {ignored.length > 0 && <Alert tone="warn" testid="text-ignored-columns">Ignored columns from {filename}: <strong>{ignored.join(", ")}</strong>. Company and allocation fields are not imported here; use Manage allocations per investor.</Alert>}
              {rows.length === 0 ? (
                <button onClick={() => fileRef.current?.click()} className={`w-full border-2 border-dashed ${C.border} rounded-xl p-10 text-center hover:border-[#C64600] ${C.focus}`}>
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${C.muted}`} aria-hidden />
                  <span className="block font-semibold">Choose a CSV file</span>
                  <span className={`block text-xs mt-1 ${C.muted}`}>Columns: name, email, phone, firm, type, notes. Or add rows by hand.</span>
                </button>
              ) : (
                <div className={`border ${C.border} rounded-xl overflow-x-auto`}>
                  <table className="w-full text-sm min-w-[860px]">
                    <thead className={`${C.sub} text-[11px] uppercase tracking-wider ${C.muted}`}>
                      <tr><th className="px-2 py-2 text-left w-10">#</th>{INVESTOR_FIELDS.map(f => <th key={f} className="px-2 py-2 text-left font-medium">{f}{(f === "name" || f === "email") && <span aria-hidden className="text-[#B42318] dark:text-[#F4B3A9]"> *</span>}</th>)}<th className="w-10"><span className="sr-only">Remove</span></th></tr>
                    </thead>
                    <tbody className={`divide-y ${C.border.replace(/border-/g, "divide-")}`}>
                      {rows.map((r, i) => (
                        <tr key={i}>
                          <td className={`px-2 py-1.5 font-mono text-xs ${C.muted}`}>{i + 2}</td>
                          {INVESTOR_FIELDS.map(f => (
                            <td key={f} className="px-1 py-1">
                              <input id={`ci-row-${i}-${f}`} value={r[f]} onChange={e => editRow(i, f, e.target.value)} aria-label={`Row ${i + 2} ${f}`} type={f === "email" ? "email" : "text"}
                                className={`w-full rounded-md border px-2 py-1.5 text-sm ${C.input}`} data-testid={`input-row-${i}-${f}`} />
                            </td>
                          ))}
                          <td className="px-1"><button onClick={() => removeRow(i)} aria-label={`Remove row ${i + 2}`} className={`p-1.5 rounded-md text-[#B42318] dark:text-[#F4B3A9] hover:bg-[#F8DAD6] dark:hover:bg-[#3D1712] ${C.focus}`} data-testid={`button-remove-row-${i}`}><Trash2 className="w-4 h-4" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {rows.length > 0 && <p className={`text-xs ${C.muted}`}>{rows.length} row{rows.length === 1 ? "" : "s"}. Any edit clears the previous preview; review again before importing.</p>}
            </section>
          )}

          {step === 3 && preview && (
            <section className="space-y-4" aria-labelledby="ci-s3">
              <h3 id="ci-s3" className="font-semibold">Review for <span className="text-[#C64600] dark:text-[#FF7A2E]">{preview.cohort.name}</span></h3>
              <div className="flex flex-wrap gap-2" data-testid="text-preview-summary">
                {Object.entries(preview.summary).map(([k, v]) => {
                  const meta = STATUS_META[k as PreviewRow["status"]];
                  return <span key={k} className={`text-xs px-2.5 py-1 rounded-full border font-medium ${meta?.cls || `${C.border} ${C.muted}`}`}>{meta?.label || k.replace(/_/g, " ")}: <strong>{v}</strong></span>;
                })}
              </div>
              {preview.warnings.map((w, i) => <Alert key={i} tone="warn">{w}</Alert>)}
              <ul className="space-y-2">
                {preview.rows.map(row => {
                  const meta = STATUS_META[row.status];
                  const selectable = canSelect(row, resolutions);
                  const on = selected.has(row.index) && selectable;
                  const editable = row.status === "blocked" || row.status === "duplicate" || row.status === "review";
                  return (
                    <li key={row.index} data-testid={`row-preview-${row.index}`} className={`rounded-xl border p-3 ${C.border} ${on ? "bg-[#FFFDF9] dark:bg-[#1F1B15]" : C.sub}`}>
                      <div className="flex items-start gap-3">
                        <input type="checkbox" className="mt-1 w-4 h-4 accent-[#C64600]" checked={on} disabled={!selectable} onChange={() => toggle(row)}
                          aria-label={`Include row ${row.rowNumber}, ${row.name || "unnamed"}`} data-testid={`checkbox-row-${row.index}`} />
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`font-mono text-xs ${C.muted}`}>Row {row.rowNumber}</span>
                            <span className="font-semibold truncate">{row.name || "(no name)"}</span>
                            <span className={`text-sm truncate ${C.muted}`}>{row.email || "(no email)"}</span>
                            <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${meta.cls}`}>{meta.label}</span>
                          </div>
                          {row.existingName && <p className={`text-xs ${C.muted}`}>Matches existing profile: <strong className="text-[#1F1B16] dark:text-[#F2EDE4]">{row.existingName}</strong>{row.investorId ? ` (ID ${row.investorId})` : ""}</p>}
                          {row.duplicateOf !== undefined && <p className={`text-xs ${C.muted}`}>Duplicates row {preview.rows.find(r => r.index === row.duplicateOf)?.rowNumber ?? row.duplicateOf}. Excluded.</p>}
                          {row.issues.length > 0 && <ul className="text-xs text-[#7A1D12] dark:text-[#F4B3A9] list-disc pl-4">{row.issues.map((s, i) => <li key={i}>{s}</li>)}</ul>}
                          {row.status === "review" && (
                            <label className="flex flex-col sm:flex-row sm:items-center gap-1.5 text-xs font-medium">
                              Decision required
                              <select value={resolutions[String(row.index)] || ""} onChange={e => setResolution(row, e.target.value)} className={`rounded-md border px-2 py-1.5 text-sm ${C.input}`} data-testid={`select-resolution-${row.index}`}>
                                <option value="">Exclude this row</option>
                                {(row.resolution ? [row.resolution] : (Object.keys(RESOLUTION_OPTIONS) as Resolution[])).map(k => <option key={k} value={k}>{RESOLUTION_OPTIONS[k]}</option>)}
                              </select>
                            </label>
                          )}
                          {(row.status === "blocked" || row.status === "duplicate") && <p className={`text-xs ${C.muted}`}>Cannot be imported as is. Edit the row and preview again, or leave it excluded.</p>}
                        </div>
                        {editable && <button onClick={() => jumpToEdit(row.index)} className={`shrink-0 text-xs px-2.5 py-1.5 rounded-lg ${C.ghost} ${C.focus}`} data-testid={`button-edit-row-${row.index}`}>Edit row</button>}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className={`rounded-xl border-2 border-[#C64600] p-4 space-y-3 bg-[#FDF1E7] dark:bg-[#2A1A0E]`}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <Figure label="Selected to import" value={finalSelection.length} testid="text-selected-count" />
                  <Figure label="Skipped" value={skipped} testid="text-skipped-count" />
                  <Figure label="Ready rows" value={preview.rows.filter(r => isReady(r.status)).length} />
                </div>
                {unresolvedSelected > 0 && <p className="text-xs text-[#6B4300] dark:text-[#F5D38A]">{unresolvedSelected} row(s) still need a decision and will not import.</p>}
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#C64600]" checked={ack} onChange={e => setAck(e.target.checked)} disabled={finalSelection.length === 0} data-testid="checkbox-acknowledge" />
                  <span>I understand only the <strong>{finalSelection.length}</strong> selected row(s) will be imported into <strong>{preview.cohort.name}</strong> as one all-or-nothing batch, <strong>{skipped}</strong> will be skipped, and all other existing investor records, memberships and allocations stay unchanged.</span>
                </label>
              </div>
            </section>
          )}

          {step === 4 && result && (
            <section className="space-y-4 text-center py-4" aria-live="polite" data-testid="text-import-result">
              <CheckCircle2 className="w-12 h-12 mx-auto text-[#1E7A44] dark:text-[#7FD3A0]" aria-hidden />
              <h3 className="text-lg font-bold">Import complete for {preview?.cohort.name}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-left max-w-2xl mx-auto">
                <Figure label="Investors created" value={result.created} />
                <Figure label="Added to cohort" value={result.added} />
                <Figure label="Already members" value={result.alreadyMembers} />
                <Figure label="Skipped" value={result.skipped} />
              </div>
              <p className={`text-sm ${C.muted}`}>No allocations were created and no emails were sent.</p>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className={`border-t ${C.border} ${C.sub} px-5 sm:px-6 py-3.5 flex flex-wrap items-center justify-between gap-2`}>
          <div>
            {step === 2 && <Btn kind="ghost" onClick={() => setStep(1)} testid="button-back"><ArrowLeft className="w-4 h-4" /> Cohort</Btn>}
            {step === 3 && <Btn kind="ghost" onClick={() => setStep(2)} disabled={committing} testid="button-back"><ArrowLeft className="w-4 h-4" /> Edit rows</Btn>}
          </div>
          <div className="flex flex-wrap gap-2">
            {step === 1 && <Btn onClick={() => setStep(2)} disabled={!cohortId} testid="button-next">Continue <ArrowRight className="w-4 h-4" /></Btn>}
            {step === 2 && <>
              {preview && <Btn kind="ghost" onClick={() => setStep(3)} testid="button-view-preview">View current preview</Btn>}
              <Btn onClick={runPreview} disabled={!rows.length || previewing} testid="button-preview">{previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {previewing ? "Checking" : "Preview import"}</Btn>
            </>}
            {step === 3 && <Btn onClick={commit} disabled={!ack || finalSelection.length === 0 || committing} testid="button-confirm-import">{committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {committing ? "Importing" : `Import ${finalSelection.length} row${finalSelection.length === 1 ? "" : "s"}`}</Btn>}
            {step === 4 && <Btn onClick={onClose} testid="button-done">Done</Btn>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Btn({ children, onClick, disabled, kind = "primary", testid }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; kind?: "primary" | "ghost"; testid?: string }) {
  return <button onClick={onClick} disabled={disabled} data-testid={testid} className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 disabled:cursor-not-allowed ${kind === "primary" ? C.primary : `${C.ghost} disabled:opacity-60`} ${C.focus}`}>{children}</button>;
}
function Figure({ label, value, testid }: { label: string; value: number; testid?: string }) {
  return <div className={`rounded-lg border ${C.border} bg-[#FFFDF9] dark:bg-[#1C1914] p-3`}><p className={`text-[10px] uppercase tracking-wider font-mono ${C.muted}`}>{label}</p><p className="text-2xl font-bold" data-testid={testid}>{value}</p></div>;
}
function Alert({ tone, children, testid }: { tone: "error" | "warn"; children: React.ReactNode; testid?: string }) {
  const cls = tone === "error" ? "bg-[#F8DAD6] text-[#7A1D12] border-[#E5A39A] dark:bg-[#3D1712] dark:text-[#F4B3A9] dark:border-[#7D3328]" : "bg-[#FCEBC7] text-[#6B4300] border-[#E8C277] dark:bg-[#3A2A0C] dark:text-[#F5D38A] dark:border-[#7A5A1C]";
  return <div role={tone === "error" ? "alert" : "status"} data-testid={testid} className={`rounded-lg border px-3 py-2 text-sm flex gap-2 ${cls}`}><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden /><div>{children}</div></div>;
}
